<template>
  <div class="app-container tracabilite-descendante">

    <!-- ══════════════════════════════════════════════════════════════
         EN-TÊTE
    ══════════════════════════════════════════════════════════════ -->
    <div class="page-header">
      <div class="page-header__left">
        <h2><i class="el-icon-bottom" /> Traçabilité Descendante</h2>
        <p class="page-header__subtitle">
          Reconstituer la chaîne : <strong>Lot → Sacs → Producteurs → Parcelles → Documents</strong>
        </p>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         PANNEAU DE RECHERCHE
    ══════════════════════════════════════════════════════════════ -->
    <el-card class="search-card" shadow="always">
      <div slot="header" class="card-header">
        <i class="el-icon-search" />
        <span>Critères de recherche</span>
      </div>

      <!-- Mode de recherche -->
      <div class="search-section">
        <p class="section-label">Mode de recherche</p>
        <el-radio-group v-model="form.mode" size="medium" @change="resetResults">
          <el-radio-button label="lot">
            <i class="el-icon-truck" /> N° de lot / camion
          </el-radio-button>
          <el-radio-button label="dates">
            <i class="el-icon-date" /> Intervalle de dates
          </el-radio-button>
          <el-radio-button label="sac">
            <i class="el-icon-box" /> N° de sac
          </el-radio-button>
          <el-radio-button label="producteur">
            <i class="el-icon-user" /> Producteur
          </el-radio-button>
          <el-radio-button label="groupement">
            <i class="el-icon-s-order" /> Groupement
          </el-radio-button>
        </el-radio-group>
      </div>

      <!-- Champs dynamiques selon le mode -->
      <div class="search-fields">

        <!-- Lot / Sac : champ texte libre -->
        <el-form v-if="form.mode === 'lot' || form.mode === 'sac'" :inline="true" class="search-form">
          <el-form-item :label="modeLabel">
            <el-input
              v-model="form.q"
              :placeholder="modePlaceholder"
              clearable
              prefix-icon="el-icon-search"
              style="width: 360px;"
              @keyup.enter.native="lancer"
            />
          </el-form-item>
        </el-form>

        <!-- Producteur : autocomplete dynamique -->
        <el-form v-if="form.mode === 'producteur'" :inline="true" class="search-form">
          <el-form-item label="Producteur">
            <el-select
              v-model="form.producteur_select"
              filterable
              remote
              clearable
              :remote-method="onProducteurInput"
              :loading="loadingProducteurs"
              placeholder="Tapez un nom, prénom ou code..."
              style="width: 360px;"
              no-data-text="Aucun producteur trouvé"
              @change="onProducteurSelect"
              @clear="onProducteurClear"
            >
              <el-option
                v-for="p in producteurs"
                :key="p.id"
                :label="p.nom + ' ' + p.prenom + (p.code ? ' · ' + p.code : '')"
                :value="p.id"
              >
                <div class="producteur-option">
                  <span class="producteur-option__name">{{ p.nom }} {{ p.prenom }}</span>
                  <el-tag v-if="p.code" size="mini" effect="plain" type="info" style="margin-left:8px;">{{ p.code }}</el-tag>
                  <span v-if="p.groupement" class="producteur-option__group">{{ p.groupement }}</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <!-- Indicateur producteur sélectionné -->
          <div v-if="form.q && !loadingProducteurs" class="selected-hint">
            <i class="el-icon-success" style="color:#67c23a;" />
            Recherche sur : <strong>{{ form.q }}</strong>
          </div>
          <div v-else-if="loadingProducteurs" class="selected-hint">
            <i class="el-icon-loading" style="color:#409eff;" /> Chargement des producteurs...
          </div>
        </el-form>

        <!-- Dates : date précise ou intervalle -->
        <el-form v-if="form.mode === 'dates'" :inline="true" class="search-form">
          <el-form-item label="Date">
            <el-date-picker
              v-model="form.date_debut"
              type="date"
              placeholder="Sélectionner une date"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              clearable
            />
          </el-form-item>
          <el-form-item>
            <template slot="label">
              Date fin
              <el-tooltip content="Laisser vide pour rechercher sur une date précise" placement="top">
                <i class="el-icon-question" style="color:#909399; cursor:help;" />
              </el-tooltip>
            </template>
            <div style="display:flex; align-items:center; gap:6px;">
              <el-date-picker
                v-model="form.date_fin"
                type="date"
                placeholder="Optionnelle"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                :picker-options="dateFinOptions"
                clearable
              />
              <el-button
                v-if="form.date_fin"
                size="mini"
                type="text"
                icon="el-icon-close"
                style="color:#f56c6c;"
                @click="form.date_fin = null"
              />
            </div>
          </el-form-item>
          <!-- Helper text -->
          <div class="dates-helper">
            <i class="el-icon-info" />
            <span v-if="!form.date_fin">Recherche sur une <strong>date précise</strong> — ajoutez une date de fin pour définir un intervalle.</span>
            <span v-else>Recherche sur l'<strong>intervalle</strong> du {{ form.date_debut | formatDate }} au {{ form.date_fin | formatDate }}.</span>
          </div>
        </el-form>

        <!-- Groupement : autocomplete dynamique -->
        <el-form v-if="form.mode === 'groupement'" :inline="true" class="search-form">
          <el-form-item label="Groupement">
            <el-select
              v-model="form.groupement_id"
              filterable
              remote
              clearable
              :remote-method="onGroupementInput"
              :loading="loadingGroupements"
              placeholder="Tapez le nom d'un groupement..."
              style="width: 360px;"
              no-data-text="Aucun groupement trouvé"
            >
              <el-option
                v-for="g in groupements"
                :key="g.id"
                :label="g.nom + (g.code ? ' · ' + g.code : '')"
                :value="g.id"
              >
                <div class="producteur-option">
                  <span class="producteur-option__name">{{ g.nom }}</span>
                  <el-tag v-if="g.code" size="mini" effect="plain" type="info" style="margin-left:8px;">{{ g.code }}</el-tag>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <!-- Filtres optionnels -->
      <el-collapse v-model="showFiltres" class="filtres-collapse">
        <el-collapse-item title="Filtres optionnels (affiner les résultats)" name="filtres">
          <el-form :inline="true" class="search-form">
            <el-form-item label="Campagne">
              <el-select v-model="form.campagne_id" clearable placeholder="Toutes les campagnes" style="width: 220px;">
                <el-option
                  v-for="c in campagnes"
                  :key="c.id"
                  :label="c.periode"
                  :value="c.id"
                />
              </el-select>
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>

      <!-- Bouton lancer -->
      <div class="search-actions">
        <el-button
          type="primary"
          icon="el-icon-search"
          size="medium"
          :loading="loading"
          @click="lancer"
        >
          {{ loading ? 'Recherche en cours...' : 'Lancer la recherche' }}
        </el-button>
        <el-button
          v-if="searched"
          icon="el-icon-refresh"
          size="medium"
          @click="reinitialiser"
        >
          Réinitialiser
        </el-button>
      </div>
    </el-card>

    <!-- ══════════════════════════════════════════════════════════════
         INDICATEUR DE CHARGEMENT
    ══════════════════════════════════════════════════════════════ -->
    <div v-if="loading" class="loading-block">
      <i class="el-icon-loading" />
      <span>Recherche en cours, veuillez patienter...</span>
    </div>

    <!-- ══════════════════════════════════════════════════════════════
         AUCUN RÉSULTAT
    ══════════════════════════════════════════════════════════════ -->
    <el-card v-if="!loading && searched && lots.length === 0" class="empty-card" shadow="never">
      <el-empty
        description="Aucune donnée ne correspond à votre recherche. Vérifiez le numéro de lot, les dates ou le nom du producteur."
      >
        <el-button type="primary" @click="reinitialiser">Modifier les critères</el-button>
      </el-empty>
    </el-card>

    <!-- ══════════════════════════════════════════════════════════════
         RÉSULTATS
    ══════════════════════════════════════════════════════════════ -->
    <template v-if="!loading && lots.length > 0">

      <!-- Bandeau résumé -->
      <div class="results-summary">
        <el-tag type="info" effect="plain" size="medium">
          <i class="el-icon-data-line" />
          {{ meta.total_lots }} lot(s) trouvé(s)
        </el-tag>
        <el-tag type="success" effect="plain" size="medium">
          <i class="el-icon-box" /> {{ meta.total_sacs }} sacs
        </el-tag>
        <el-tag type="warning" effect="plain" size="medium">
          <i class="el-icon-user" /> {{ meta.total_producteurs }} producteurs
        </el-tag>
        <el-tag type="danger" effect="plain" size="medium">
          <i class="el-icon-map-location" /> {{ meta.total_parcelles }} parcelles
        </el-tag>
        <el-tag effect="plain" size="medium">
          Affichage : {{ lots.length }} lot(s) sur {{ meta.total_lots }}
        </el-tag>
      </div>

      <!-- Accordéon des lots -->
      <el-collapse v-model="openLots" class="lots-collapse">
        <el-collapse-item
          v-for="lot in lots"
          :key="lot.id"
          :name="String(lot.id)"
          class="lot-item"
        >
          <!-- En-tête du lot -->
          <template slot="title">
            <div class="lot-header">
              <span class="lot-code">
                <i class="el-icon-truck" /> {{ lot.code || lot.im_camion }}
              </span>
              <span class="lot-date">{{ lot.date_chrgmnt }}</span>
              <el-tag size="small" type="info">{{ lot.sacs_count }} sacs</el-tag>
              <el-tag size="small" type="success">{{ lot.poids_total || '—' }} T</el-tag>
              <el-tag v-if="lot.campagne" size="small">{{ lot.campagne.periode }}</el-tag>
              <el-tag v-if="lot.produit" size="small" type="warning">{{ lot.produit.nom }}</el-tag>
            </div>
          </template>

          <!-- Contenu du lot -->
          <div class="lot-body">
            <!-- Informations lot -->
            <el-descriptions :column="3" border size="mini" class="lot-details">
              <el-descriptions-item label="Code lot">{{ lot.code }}</el-descriptions-item>
              <el-descriptions-item label="Immatriculation">{{ lot.im_camion }}</el-descriptions-item>
              <el-descriptions-item label="Société de transport">{{ lot.ste_trans }}</el-descriptions-item>
              <el-descriptions-item label="Date chargement">{{ lot.date_chrgmnt }}</el-descriptions-item>
              <el-descriptions-item label="Poids total">{{ lot.poids_total ? lot.poids_total + ' T' : '—' }}</el-descriptions-item>
              <el-descriptions-item label="Produit">{{ lot.produit ? lot.produit.nom : '—' }}</el-descriptions-item>
            </el-descriptions>

            <!-- Boutons chaîne -->
            <div class="lot-chain">
              <el-button
                size="small"
                icon="el-icon-box"
                @click="voirSacs(lot)"
              >
                Sacs ({{ lot.sacs_count }})
              </el-button>
              <el-button
                size="small"
                icon="el-icon-user"
                @click="voirProducteurs(lot)"
              >
                Producteurs ({{ lot.producteurs_count }})
              </el-button>
              <el-button
                size="small"
                icon="el-icon-map-location"
                @click="loadParcellesDuLot(lot)"
              >
                Parcelles ({{ lot.parcelles_count }})
              </el-button>
            </div>

            <!-- Documents -->
            <div class="lot-documents">
              <p class="docs-title"><i class="el-icon-document" /> Documents</p>
              <div class="docs-list">
                <div class="doc-item">
                  <span><i class="el-icon-document-checked" /> Fiche de collecte</span>
                  <div class="doc-actions-inline">
                    <el-button size="mini" type="primary" plain @click="voirDocument(lot.documents.fiche_collecte)">
                      <i class="el-icon-view" /> Voir
                    </el-button>
                    <el-button size="mini" type="success" plain @click="telecharger(lot.documents.fiche_collecte, 'fiche_collecte_' + lot.code + '.pdf')">
                      <i class="el-icon-download" /> Télécharger
                    </el-button>
                  </div>
                </div>
                <div class="doc-item">
                  <span><i class="el-icon-document-checked" /> Bon de livraison</span>
                  <div class="doc-actions-inline">
                    <el-button size="mini" type="primary" plain @click="voirDocument(lot.documents.bon_livraison)">
                      <i class="el-icon-view" /> Voir
                    </el-button>
                    <el-button size="mini" type="success" plain @click="telecharger(lot.documents.bon_livraison, 'bon_livraison_' + lot.code + '.pdf')">
                      <i class="el-icon-download" /> Télécharger
                    </el-button>
                  </div>
                </div>

                <!-- Liens Dolibarr SAU -->
                <div v-if="loadingSauLinks[lot.id]" class="doc-item">
                  <span><i class="el-icon-loading" /> Recherche liens Dolibarr SAU...</span>
                </div>
                <template v-else-if="lotsSauLinks[lot.id] && lotsSauLinks[lot.id].length > 0">
                  <div v-for="(link, index) in lotsSauLinks[lot.id]" :key="'sau-'+lot.id+'-'+index" class="doc-item">
                    <span>
                      <el-tag size="mini" type="primary" effect="dark" style="margin-right: 8px;">SAU</el-tag>
                      <i class="el-icon-link" /> {{ formatLinkName(link.dol_object_type) }} {{ link.ref ? '(' + link.ref + ')' : '' }}
                    </span>
                    <div class="doc-actions-inline">
                      <el-button size="mini" type="primary" @click="afficherDolibarr(link, 'sau',lot.id)">
                        <i class="el-icon-view" /> Détails
                      </el-button>
                    </div>
                  </div>
                </template>

                <!-- Liens Dolibarr TRADING -->
                <div v-if="loadingTradingLinks[lot.id]" class="doc-item">
                  <span><i class="el-icon-loading" /> Recherche liens Dolibarr TRADING...</span>
                </div>
                <template v-else-if="lotsTradingLinks[lot.id] && lotsTradingLinks[lot.id].length > 0">
                  <div v-for="(link, index) in lotsTradingLinks[lot.id]" :key="'trading-' + lot.id + '-' + index" class="doc-item">
                    <span>
                      <el-tag size="mini" type="success" effect="dark" style="margin-right: 8px;">TRADING</el-tag>
                      <i class="el-icon-link" /> {{ formatLinkName(link.dol_object_type) }} {{ link.ref ? '(' + link.ref + ')' : '' }}
                    </span>
                    <div class="doc-actions-inline">
                      <el-button size="mini" type="primary" @click="afficherDolibarr(link, 'trading',lot.id)">
                        <i class="el-icon-view" /> Détails
                      </el-button>
                    </div>
                  </div>
                </template>
              </div>
            </div>

          </div>
        </el-collapse-item>
      </el-collapse>

      <!-- Pagination -->
      <div v-if="pagination.total > pagination.per_page" class="pagination-container">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :current-page.sync="pagination.current_page"
          :page-size="pagination.per_page"
          :total="pagination.total"
          @current-change="handlePageChange"
        />
      </div>
    </template>

    <!-- ══════════════════════════════════════════════════════════════
         DRAWER – SACS D'UN LOT
    ══════════════════════════════════════════════════════════════ -->
    <el-drawer
      :title="'Sacs – ' + (drawerLot ? drawerLot.code : '')"
      :visible.sync="drawer.sacs"
      direction="rtl"
      size="70%"
    >
      <div v-loading="drawer.loading" class="drawer-content">
        <div v-if="drawerData.sacs" class="drawer-summary">
          <el-tag type="info">{{ drawerData.sacs.total_sacs }} sacs</el-tag>
          <el-tag type="success">{{ (drawerData.sacs.total_poids / 1000).toFixed(2) }} T total</el-tag>
          <el-button size="mini" icon="el-icon-download" style="float:right" @click="exporterTableau(drawerData.sacs)">
            Exporter la liste
          </el-button>
        </div>
        <el-table
          v-if="drawerData.sacs"
          :data="drawerData.sacs.data"
          border
          stripe
          size="mini"
          style="width: 100%; margin-top: 12px;"
        >
          <el-table-column label="Code sac" prop="code" min-width="120" />
          <el-table-column label="Producteur" min-width="180">
            <template slot-scope="{ row }">{{ row.producteur_nom || '—' }}</template>
          </el-table-column>
          <el-table-column label="Code producteur" prop="producteur_code" min-width="130" />
          <el-table-column label="Parcelle" prop="parcelle_code" min-width="120" />
          <el-table-column label="Poids (Kg)" prop="poids" min-width="100" />
          <el-table-column label="Date récolte" prop="date_recolte" min-width="120" />
        </el-table>
      </div>
    </el-drawer>

    <!-- ══════════════════════════════════════════════════════════════
         DRAWER – PRODUCTEURS D'UN LOT
    ══════════════════════════════════════════════════════════════ -->
    <el-drawer
      :title="'Producteurs – ' + (drawerLot ? drawerLot.code : '')"
      :visible.sync="drawer.producteurs"
      direction="rtl"
      size="65%"
    >
      <div v-loading="drawer.loading" class="drawer-content">
        <div v-if="drawerData.producteurs" class="drawer-summary">
          <el-tag type="warning">{{ drawerData.producteurs.total_producteurs }} producteurs</el-tag>
          <el-button type="primary" icon="el-icon-download" @click="dowloadAllProducteurDocuments(drawerData.producteurs)">Télécharger tous les documents</el-button>
        </div>
        <!-- Loader de téléchargement -->
        <div v-if="downloadLoading" class="download-loader">
          <el-progress :percentage="downloadProgress" :show-text="true" />
          <p>Téléchargement des documents en cours... {{ downloadProgress }}%</p>
        </div>
        <el-table
          v-if="drawerData.producteurs"
          :data="drawerData.producteurs.data"
          border
          stripe
          size="mini"
          style="width: 100%; margin-top: 12px;"
        >
          <el-table-column label="Code" prop="code" min-width="100" />
          <el-table-column label="Nom" prop="nom" min-width="120" />
          <el-table-column label="Prénom" prop="prenom" min-width="120" />
          <el-table-column label="Groupement" prop="groupement_nom" min-width="160" />
          <el-table-column label="Nb sacs" prop="nombre_sacs" min-width="80" />
          <el-table-column label="Poids (Kg)" prop="poids_total" min-width="100" />
          <el-table-column label="Fiche Contrat" min-width="100">
            <template slot-scope="{ row }">
              <template v-if="row.fiche_contrat">
                <el-button size="mini" type="primary" icon="el-icon-download" @click="telechargerFicheContrat(row)" />
                <el-button size="mini" type="secondary" icon="el-icon-view" @click="voirDocument(row.fiche_contrat)" />
              </template>
              <template v-else>
                <el-tag size="mini" type="info">—</el-tag>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="Fiche d'id parcel" min-width="100">
            <template slot-scope="{ row }">
              <template v-if="row.fiche_identification">
                <el-button size="mini" type="success" icon="el-icon-download" @click="telechargerFicheIdParcellaire(row)" />
                <el-button size="mini" type="secondary" icon="el-icon-view" @click="voirDocument(row.fiche_identification)" />
              </template>
              <template v-else>
                <el-tag size="mini" type="info">—</el-tag>
              </template>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>

    <!-- ══════════════════════════════════════════════════════════════
         DRAWER – PARCELLES D'UN LOT
    ══════════════════════════════════════════════════════════════ -->
    <el-drawer
      :title="'Parcelles – ' + (drawerLot ? drawerLot.code : '')"
      :visible.sync="drawer.parcelles"
      direction="rtl"
      size="70%"
    >
      <div v-loading="drawer.loading" class="drawer-content">
        <div v-if="drawerData.parcelles" class="drawer-summary">
          <el-tag type="danger">{{ drawerData.parcelles.total_parcelles }} parcelles</el-tag>
          <div style="display: flex; gap: 10px">
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-map-location"
              style="float:right"
              @click="voirCarte()"
            >
              Voir sur la carte
            </el-button>
            <el-button
              size="mini"
              type="success"
              icon="el-icon-download"
              style="float:right"
              @click="downloadAllParcellesCtrlIntFiles(drawerData.parcelles)"
            >
              Télécharger Tout les Contrôles internes
            </el-button>
          </div>
        </div>
        <!-- Loader de téléchargement -->
        <div v-if="downloadLoading" class="download-loader">
          <el-progress :percentage="downloadProgress" :show-text="true" />
          <p>Téléchargement des documents en cours... {{ downloadProgress }}%</p>
        </div>
        <el-table
          v-if="drawerData.parcelles"
          :data="drawerData.parcelles.data"
          border
          stripe
          size="mini"
          style="width: 100%; margin-top: 12px;"
        >
          <el-table-column label="Code parcelle" prop="code" min-width="130" />
          <el-table-column label="Producteur" prop="producteur" min-width="160" />
          <el-table-column label="Superficie (ha)" prop="superficie" min-width="120" />
          <el-table-column label="Nb sacs" prop="nombre_sacs" min-width="80" />
          <el-table-column label="Poids (T)" prop="poids_total" min-width="100" />
          <el-table-column label="GPS" min-width="80">
            <template slot-scope="{ row }">
              <el-button v-if="row.gps" size="mini" type="success" icon="el-icon-location" @click="voirCarteParcelle(row)" />
              <el-tag v-else size="mini" type="info">—</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Contrôle interne" min-width="80">
            <template slot-scope="{ row }">
              <el-button v-if="row.controle_interne_url" size="mini" type="primary" icon="el-icon-document" @click="voirControleInterne(row)" />
              <el-tag v-else size="mini" type="info">—</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>

    <!-- Modal pour la carte des parcelles -->
    <parcelle-map
      :visible.sync="mapVisible"
      :parcelles="mapParcelles"
    />

    <!-- Modal Fiche BL -->
    <el-dialog :title="'Informations Fiche BL (' + (fichebl.source || '') + ')'" :visible.sync="ficheBLDialog" width="50%" append-to-body>
      <div v-loading="ficheBLLoading">
        <div v-if="fichebl && fichebl.id">
          <table class="border centpercent" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 35%;">Référence BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.ref }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Réf client</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.ref_client || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Exportateur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.exportateur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Destinataire</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.destinataire }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">N° de BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.numero_bl || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Nb de conteneurs</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.nb_conteneur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Tonnage BL</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.tonnage_bl }} t</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Port de départ</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.port_depart }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Port de livraison</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.port_livraison }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">ETD Bateau</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.etd_bateau | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">ETA Bateau</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.eta_bateau | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Compagnie Maritime</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.compagnie_maritime }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Transitaire</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.transitaire }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Point de stockage</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ fichebl.point_stockage }}</td>
            </tr>
          </table>
          <div style="margin-top: 20px; text-align: center;">
            <el-link :href="fichebl.web_url" target="_blank" type="primary" icon="el-icon-link">Ouvrir dans {{ fichebl.source }} (Dolibarr)</el-link>
          </div>
        </div>
        <div v-else-if="!ficheBLLoading" style="text-align: center; padding: 20px;">
          <el-alert title="Aucun Bon de Livraison lié trouvé pour ce lot." type="info" show-icon />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="ficheBLDialog = false">Fermer</el-button>
      </span>
    </el-dialog>

    <!-- Modal Commande -->
    <el-dialog :title="'Informations Commande (' + (commande.source || '') + ')'" :visible.sync="commandeDialog" width="50%" append-to-body>
      <div v-loading="commandeLoading">
        <div v-if="commande && commande.id">
          <table class="border centpercent" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 35%;">Référence Interne</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.ref }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Réf. Fournisseur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.ref_supplier || 'N/A' }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Fournisseur</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.fournisseur }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Statut</td>
              <td style="padding: 8px; border: 1px solid #ddd;">
                <el-tag size="small">{{ commande.statut_label }}</el-tag>
              </td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Date commande</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.date_commande | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Date livraison prévue</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.date_livraison | formatDate }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total HT</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_ht }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total TVA</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_tva }}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Total TTC</td>
              <td style="padding: 8px; border: 1px solid #ddd;">{{ commande.total_ttc }}</td>
            </tr>
          </table>
          <div style="margin-top: 20px; text-align: center;">
            <el-link :href="commande.web_url" target="_blank" type="primary" icon="el-icon-link">Ouvrir dans {{ commande.source }} (Dolibarr)</el-link>
          </div>
        </div>
        <div v-else-if="!commandeLoading" style="text-align: center; padding: 20px;">
          <el-alert title="Aucune Commande liée trouvée pour ce lot." type="info" show-icon />
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="commandeDialog = false">Fermer</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import tracabiliteApi from '@/api/tracabilite';
import JSZip from 'jszip';
import Resource from '@/api/resource';
import ParcelleMap from '@/components/ParcelleMap.vue';

const campagneResource = new Resource('campagne_agricoles');
const groupementResource = new Resource('groupements');
const producteurResource = new Resource('producteurs-all');

export default {
  name: 'TracabiliteDescendante',
  components: {
    ParcelleMap,
  },

  filters: {
    formatDate(value) {
      if (!value) {
        return '';
      }
      if (typeof value !== 'string') {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
          return value;
        }
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
      const parts = value.split('-');
      if (parts.length < 3) {
        return value;
      }
      const [y, m, d] = parts;
      return `${d}/${m}/${y}`;
    },
  },

  data() {
    return {
      // ── Formulaire de recherche ─────────────────────────────────
      form: {
        mode: 'lot',
        q: '', // terme de recherche texte (lot, sac, producteur nom)
        producteur_select: null, // id sélectionné dans l'autocomplete producteur
        date_debut: null,
        date_fin: null,
        campagne_id: null,
        groupement_id: null,
      },

      // ── États UI ────────────────────────────────────────────────
      loading: false,
      searched: false,
      showFiltres: [],

      // ── Données résultats ───────────────────────────────────────
      lots: [],
      meta: {
        total_lots: 0,
        total_sacs: 0,
        total_producteurs: 0,
        total_parcelles: 0,
      },
      openLots: [],
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
      },

      // ── Données des selects ─────────────────────────────────────
      campagnes: [],
      groupements: [],
      producteurs: [],
      loadingGroupements: false,
      loadingProducteurs: false,
      debounceGroupement: null,
      debounceProducteur: null,

      // ── Drawers (panneaux détails) ──────────────────────────────
      drawer: {
        sacs: false,
        producteurs: false,
        parcelles: false,
        loading: false,
      },
      drawerLot: null,
      drawerData: {
        sacs: null,
        producteurs: null,
        parcelles: null,
      },

      // ── Carte ───────────────────────────────────────────────────
      mapVisible: false,
      mapParcelles: [],

      // Dolibarr Integration
      ficheBLDialog: false,
      fichebl: {},
      ficheBLLoading: false,
      commandeDialog: false,
      commande: {},
      commandeLoading: false,
      lotsSauLinks: {},
      lotsTradingLinks: {},
      loadingSauLinks: {},
      loadingTradingLinks: {},
      loadingLinks: {},
      dolibarrApiKey: '',
      sauUrl: '',
      tradingUrl: '',

      // ── Téléchargement ─────────────────────────────────────────────
      downloadLoading: false,
      downloadProgress: 0,

      // Options date picker
      dateFinOptions: {
        disabledDate: (date) => {
          if (!this.form.date_debut) {
            return false;
          }
          return date < new Date(this.form.date_debut);
        },
      },
    };
  },

  computed: {
    modeLabel() {
      const labels = {
        lot: 'N° de lot / camion',
        sac: 'N° de sac',
        producteur: 'Producteur',
      };
      return labels[this.form.mode] || 'Recherche';
    },
    modePlaceholder() {
      const placeholders = {
        lot: 'Ex: 24-LOT001 ou immatriculation camion...',
        sac: 'Ex: code du sac...',
        producteur: 'Nom, prénom ou code producteur...',
      };
      return placeholders[this.form.mode] || 'Rechercher...';
    },
  },

  created() {
    this.loadCampagnes();
  },

  methods: {

    // ── Chargement des données référentielles ─────────────────────
    async loadCampagnes() {
      try {
        const { data } = await campagneResource.list({ limit: 50 });
        this.campagnes = data || [];
      } catch (e) {
        console.error('Erreur chargement campagnes', e);
      }
    },

    // ── Autocomplete groupement avec debounce 300ms ───────────────
    onGroupementInput(query) {
      clearTimeout(this.debounceGroupement);
      if (!query || query.length < 1) {
        this.groupements = [];
        return;
      }
      this.debounceGroupement = setTimeout(() => {
        this.searchGroupements(query);
      }, 300);
    },

    async searchGroupements(query) {
      this.loadingGroupements = true;
      try {
        const { data } = await groupementResource.list({ keyword: query, limit: 30 });
        this.groupements = data || [];
      } catch (e) {
        console.error('Erreur recherche groupements', e);
      } finally {
        this.loadingGroupements = false;
      }
    },

    // ── Autocomplete producteur avec debounce 300ms ───────────────
    onProducteurInput(query) {
      clearTimeout(this.debounceProducteur);
      if (!query || query.length < 1) {
        this.producteurs = [];
        this.form.q = '';
        return;
      }
      this.debounceProducteur = setTimeout(() => {
        this.searchProducteurs(query);
      }, 300);
    },

    async searchProducteurs(query) {
      this.loadingProducteurs = true;
      try {
        const { data } = await producteurResource.list({ keyword: query, limit: 30 });
        // Normaliser : liste-simple peut retourner des formats variés
        this.producteurs = (data || []).map(p => ({
          id: p.id,
          nom: p.nom || '',
          prenom: p.prenom || '',
          code: p.code || null,
          groupement: p.groupement ? p.groupement.nom : null,
        }));
      } catch (e) {
        console.error('Erreur recherche producteurs', e);
      } finally {
        this.loadingProducteurs = false;
      }
    },

    onProducteurSelect(producteurId) {
      const produit = this.producteurs.find(p => p.id === producteurId);
      if (produit) {
        // On stocke le nom complet comme terme de recherche pour le backend
        this.form.q = produit.nom + ' ' + produit.prenom;
      }
    },

    onProducteurClear() {
      this.form.q = '';
      this.form.producteur_select = null;
      this.producteurs = [];
    },

    // ── Lancement de la recherche ─────────────────────────────────
    async lancer(page = 1) {
      // Validation côté client
      if ((this.form.mode === 'lot' || this.form.mode === 'sac') && !this.form.q.trim()) {
        this.$message({ type: 'warning', message: 'Veuillez saisir un terme de recherche.' });
        return;
      }
      if (this.form.mode === 'producteur' && !this.form.q.trim()) {
        this.$message({ type: 'warning', message: 'Veuillez sélectionner un producteur dans la liste.' });
        return;
      }
      if (this.form.mode === 'dates' && !this.form.date_debut) {
        this.$message({ type: 'warning', message: 'Veuillez renseigner au moins la date de début.' });
        return;
      }
      if (this.form.mode === 'groupement' && !this.form.groupement_id) {
        this.$message({ type: 'warning', message: 'Veuillez sélectionner un groupement.' });
        return;
      }

      this.loading = true;
      if (page === 1) {
        this.searched = false;
        this.lots = [];
      }

      this.lotsSauLinks = {};
      this.lotsTradingLinks = {};
      this.loadingSauLinks = {};
      this.loadingTradingLinks = {};
      this.loadingLinks = {};

      try {
        const params = {
          mode: this.form.mode,
          q: this.form.q || undefined,
          date_debut: this.form.date_debut || undefined,
          date_fin: this.form.date_fin || undefined,
          campagne_id: this.form.campagne_id || undefined,
          groupement_id: this.form.groupement_id || undefined,
          page: page,
          limit: this.pagination.per_page,
        };

        const response = await tracabiliteApi.searchDescendante(params);

        this.lots = response.lots || [];
        this.meta = response.meta || {};
        this.searched = true;

        if (response.pagination) {
          this.pagination.total = response.pagination.total;
          this.pagination.current_page = response.pagination.current_page;
          this.pagination.last_page = response.pagination.last_page;
        }

        // Ouvrir automatiquement le premier lot si un seul résultat au total
        if (this.lots.length === 1 && this.pagination.total === 1) {
          this.openLots = [String(this.lots[0].id)];
        }

        // Récupérer les liens Dolibarr pour tous les lots trouvés
        if (this.lots.length > 0) {
          this.getDolibarrLinksForLots(this.lots);
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erreur lors de la recherche.';
        this.$message({ type: 'error', message: msg });
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(val) {
      this.lancer(val);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    },

    resetResults() {
      this.lots = [];
      this.searched = false;
      this.form.q = '';
      this.form.producteur_select = null;
      this.form.date_debut = null;
      this.form.date_fin = null;
      this.form.groupement_id = null;
      this.producteurs = [];
      this.groupements = [];
    },

    reinitialiser() {
      this.resetResults();
      this.form.campagne_id = null;
      this.form.mode = 'lot';
    },

    // ── Drawers détail ────────────────────────────────────────────
    async voirSacs(lot) {
      this.drawerLot = lot;
      this.drawerData.sacs = null;
      this.drawer.loading = true;
      this.drawer.sacs = true;
      try {
        const response = await tracabiliteApi.getSacsDuLot(lot.id);
        this.drawerData.sacs = response;
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors du chargement des sacs.' });
        this.drawer.sacs = false;
      } finally {
        this.drawer.loading = false;
      }
    },

    async voirProducteurs(lot) {
      this.drawerLot = lot;
      this.drawerData.producteurs = null;
      this.drawer.loading = true;
      this.drawer.producteurs = true;
      try {
        const response = await tracabiliteApi.getProducteursDuLot(lot.id);
        this.drawerData.producteurs = response;
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors du chargement des producteurs.' });
        this.drawer.producteurs = false;
      } finally {
        this.drawer.loading = false;
      }
    },

    async loadParcellesDuLot(lot) {
      this.drawer.loading = true;
      this.drawer.parcelles = true;
      try {
        const response = await tracabiliteApi.getParcellesDuLot(lot.id);
        console.log('Response parcelles:', response);
        this.drawerData.parcelles = {
          data: response,
          total_parcelles: response.length,
        };
      } catch (e) {
        console.error('Erreur loadParcellesDuLot:', e);
        this.$message({ type: 'error', message: 'Erreur lors du chargement des parcelles.' });
        this.drawer.parcelles = false;
      } finally {
        this.drawer.loading = false;
      }
    },

    // ── Documents ─────────────────────────────────────────────────
    voirDocument(url) {
      window.open(url, '_blank');
    },

    async telecharger(url, filename) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Erreur réseau');
        }
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors du téléchargement.' });
      }
    },

    // ── Carte ─────────────────────────────────────────────────────
    async voirCarte() {
      try {
        // const response = await tracabiliteApi.getParcellesDuLot(lotId);
        this.mapParcelles = this.drawerData.parcelles.data; // TODO : formatter les données pour la carte
        console.log(this.mapParcelles);
        this.mapVisible = true;
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles:', error);
        this.$message({ type: 'error', message: 'Impossible de charger les données des parcelles.' });
      }
    },
    voirCarteParcelle(parcelle) {
      try {
        this.mapParcelles = [parcelle];
        console.log(this.mapParcelles);
        this.mapVisible = true;
      } catch (error) {
        console.error('Erreur lors de l\'affichage de la carte:', error);
        this.$message({ type: 'error', message: 'Impossible d\'afficher la carte.' });
      }
    },
    voirControleInterne(parcelle) {
      if (parcelle.controle_interne_url) {
        window.open(parcelle.controle_interne_url, '_blank');
      }
    },
    // ── Export tableau ────────────────────────────────────────────
    exporterTableau(sacs) {
      if (!sacs || sacs.length === 0) {
        this.$message({ type: 'warning', message: 'Aucun sac à exporter.' });
        return;
      }
      var code_lot = this.drawerLot ? this.drawerLot.code : '';

      try {
        // Préparer les données pour l'export
        const exportData = sacs.data.map(sac => ({
          'N° Sac': sac.numero_sac || sac.code || '',
          'Producteur': sac.producteur_nom || '',
          'Parcelle': sac.parcelle_code || '',
          'Poids (Kg)': sac.poids || 0,
          'Date Récolte': sac.date_recolte ? new Date(sac.date_recolte).toLocaleDateString('fr-FR') : '',
        }));

        // Créer le contenu Excel (format HTML table)
        const headers = Object.keys(exportData[0]);
        const tableContent = `
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${exportData.map(row => `<tr>
                  ${headers.map(header => `<td>${row[header]}</td>`).join('')}
                </tr>`).join('')}
            </tbody>
          </table>`;

        // Créer le contenu HTML complet pour Excel
        const htmlContent = `
          <html>
            <head>
              <meta charset="utf-8">
              <style>
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .number { text-align: right; }
              </style>
            </head>
            <body>
              ${tableContent}
            </body>
          </html>
        `;

        // Créer le blob et déclencher le téléchargement
        const blob = new Blob([htmlContent], {
          type: 'application/vnd.ms-excel;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        // Générer un nom de fichier avec le code du lot
        const lotCode = code_lot || 'lot';
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('href', url);
        link.setAttribute('download', `export_sacs_${lotCode}_${date}.xls`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Nettoyer
        URL.revokeObjectURL(url);
        this.$message({
          type: 'success',
          message: `${sacs.data.length} sac(s) exporté(s) avec succès.`,
        });
      } catch (error) {
        console.error('Erreur lors de l\'export des sacs:', error);
        this.$message({
          type: 'error',
          message: `Erreur lors de l'export: ${error.message || 'Erreur inconnue'}`,
        });
      }
    },

    // ── Téléchargement des fiches producteurs ───────────────────────
    async telechargerFicheContrat(producteur) {
      const contratUrl = producteur.fiche_contrat;
      if (!contratUrl) {
        this.$message({ type: 'warning', message: 'Aucune fiche contrat disponible pour ce producteur.' });
        return;
      }
      // Extraire le nom du fichier depuis l'URL
      const filename = contratUrl.split('/').pop() || 'fiche_contrat.pdf';
      try {
        const response = await fetch(contratUrl, {
          headers: {
            'Accept': 'application/pdf',
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message({ type: 'success', message: 'Fiche contrat téléchargée avec succès.' });
      } catch (error) {
        console.error('Erreur téléchargement fiche contrat:', error);
        this.$message({ type: 'error', message: `Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}` });
      }
    },

    async telechargerFicheIdParcellaire(producteur) {
      // exemple de lien : fiches/2023-2024/identification-parcelles/SOYATK/UP000009/aami/PRODUCTEUR-PARCELLES--100005--2023-2024--v1.pdf
      // recuperer le nom du fichier ex! filename = PRODUCTEUR-PARCELLES--100005--2023-2024--v1.pdf
      const idParcellaireUrl = producteur.fiche_identification;
      if (!idParcellaireUrl) {
        this.$message({ type: 'warning', message: 'Aucune fiche d\'identification parcellaire disponible pour ce producteur.' });
        return;
      }
      // Extraire le nom du fichier depuis l'URL
      const filename = idParcellaireUrl.split('/').pop() || 'fiche_identification.pdf';
      try {
        // Ajouter les en-têtes pour l'authentification si nécessaire
        const response = await fetch(idParcellaireUrl, {
          headers: {
            'Accept': 'application/pdf',
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message({ type: 'success', message: 'Fiche d\'identification parcellaire téléchargée avec succès.' });
      } catch (error) {
        console.error('Erreur téléchargement fiche id parcellaire:', error);
        this.$message({ type: 'error', message: `Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}` });
      }
    },

    async dowloadAllProducteurDocuments(producteurs) {
      try {
        this.downloadLoading = true;
        this.downloadProgress = 0;
        const zip = new JSZip();
        const downloadPromises = [];
        const producteursData = producteurs.data || producteurs;
        producteursData.forEach((producteur) => {
          if (producteur.fiche_contrat) {
            downloadPromises.push(
              fetch(producteur.fiche_contrat)
                .then(async(response) => {
                  if (!response.ok) {
                    console.warn(`Erreur contrat: ${producteur.fiche_contrat}`);
                    return;
                  }
                  const blob = await response.blob();
                  const filename = producteur.fiche_contrat.split('/').pop() || 'fiche_contrat.pdf';
                  zip.file(`contrats/${filename}`, blob);
                })
                .catch(err => console.warn('Fetch contrat failed:', err))
            );
          }
          if (producteur.fiche_identification) {
            downloadPromises.push(
              fetch(producteur.fiche_identification)
                .then(async(response) => {
                  if (!response.ok) {
                    console.warn(`Erreur identification: ${producteur.fiche_identification}`);
                    return;
                  }
                  const blob = await response.blob();
                  const filename = producteur.fiche_identification.split('/').pop() || 'fiche_identification.pdf';
                  zip.file(`identifications/${filename}`, blob);
                })
                .catch(err => console.warn('Fetch identification failed:', err))
            );
          }
        });
        const totalDownloads = downloadPromises.length;
        if (totalDownloads === 0) {
          this.downloadLoading = false;
          this.$message({ type: 'warning', message: 'Aucun document à télécharger.' });
          return;
        }
        let completed = 0;
        await Promise.all(
          downloadPromises.map(p =>
            p.catch(() => {})
              .then(() => {
                completed++;
                this.downloadProgress = Math.round((completed / totalDownloads) * 100);
              })
          )
        );
        const lotCode = producteurs.lot_code || 'lot';
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(zipBlob);
        link.href = url;
        link.download = `Fiches_Producteurs_${lotCode}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.downloadLoading = false;
        this.downloadProgress = 0;
        this.$message({ type: 'success', message: 'Téléchargement terminé.' });
      } catch (error) {
        console.error(error);
        this.downloadLoading = false;
        this.downloadProgress = 0;
        this.$message({
          type: 'error',
          message: error.message || 'Erreur inconnue',
        });
      }
    },
    async downloadAllParcellesCtrlIntFiles(parcelle) {
      try {
        this.downloadLoading = true;
        this.downloadProgress = 0;
        const zip = new JSZip();
        const downloadPromises = [];
        const parcellesData = parcelle.data || parcelle;
        parcellesData.forEach((parcelleItem) => {
          if (parcelleItem.controle_interne_url) {
            downloadPromises.push(
              fetch(parcelleItem.controle_interne_url)
                .then(async(response) => {
                  if (!response.ok) {
                    console.warn(`Erreur contrôle interne: ${parcelleItem.controle_interne_url}`);
                    return;
                  }
                  const blob = await response.blob();
                  const filename = parcelleItem.controle_interne_url.split('/').pop() || `controle_interne_${parcelleItem.code}.pdf`;
                  zip.file(`controles_internes/${filename}`, blob);
                })
                .catch(err => console.warn('Fetch contrôle interne failed:', err))
            );
          }
        });
        const totalDownloads = downloadPromises.length;
        if (totalDownloads === 0) {
          this.downloadLoading = false;
          this.$message({ type: 'warning', message: 'Aucun contrôle interne à télécharger.' });
          return;
        }
        let completed = 0;
        await Promise.all(
          downloadPromises.map(p =>
            p.catch(() => {})
              .then(() => {
                completed++;
                this.downloadProgress = Math.round((completed / totalDownloads) * 100);
              })
          )
        );
        const code_lot = this.drawerLot ? this.drawerLot.code : '';
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(zipBlob);
        link.href = url;
        link.download = `Controles_Internes_parcelles_${code_lot}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.downloadLoading = false;
        this.downloadProgress = 0;
        this.$message({ type: 'success', message: 'Téléchargement des contrôles internes terminé.' });
      } catch (error) {
        console.error(error);
        this.downloadLoading = false;
        this.downloadProgress = 0;
        this.$message({
          type: 'error',
          message: error.message || 'Erreur inconnue',
        });
      }
    },

    // ── Dolibarr Integration Methods ──────────────────────────────
    async getDolibarrLinksForLots(lots) {
      try {
        // Charger d'abord les credentials si on ne les a pas
        if (!this.dolibarrApiKey) {
          const response = await window.axios.get('/api/settings/api-key');
          this.dolibarrApiKey = response.data.data.api_key;
          this.sauUrl = response.data.data.dolibarr_url;
          this.tradingUrl = response.data.data.dolibarr_url_trading;
        }

        if (!this.dolibarrApiKey) {
          console.warn('Dolibarr integration not configured.');
          return;
        }

        // Récupérer les liens Dolibarr pour tous les lots trouvés
        lots.forEach((lot) => {
          this.checkLinksForLot(lot, 'sau');
          if (this.tradingUrl) {
            this.checkLinksForLot(lot, 'trading');
          }
        });
      } catch (error) {
        console.error('Erreur initialisation Dolibarr:', error);
      }
    },

    async checkLinksForLot(lot, source) {
      const url = source === 'sau' ? this.sauUrl : this.tradingUrl;
      const apiUrl = `${url}/custom/aaslink/api/get_lot_links.php`;
      const loadingState = source === 'sau' ? this.loadingSauLinks : this.loadingTradingLinks;

      try {
        this.$set(loadingState, lot.id, true);
        const response = await window.axios.get(apiUrl, {
          params: { lot_id: lot.id },
          headers: { 'X-API-KEY': this.dolibarrApiKey },
        });
        if (response.data.status === 'success' && response.data.data && response.data.data.length > 0) {
          if (source === 'sau') {
            this.$set(this.lotsSauLinks, lot.id, response.data.data);
          } else {
            this.$set(this.lotsTradingLinks, lot.id, response.data.data);
          }
        }
      } catch (e) {
        console.warn(`Erreur links Dolibarr ${source} pour lot ${lot.id}:`, e);
      } finally {
        this.$set(loadingState, lot.id, false);
      }
    },

    formatLinkName(type) {
      if (type === 'fichebl') {
        return 'Fiche BL';
      }
      return 'Commande';
    },

    async afficherDolibarr(link, source, lotId) {
      const url = source === 'sau' ? this.sauUrl : this.tradingUrl;

      if (link.dol_object_type === 'fichebl') {
        this.ficheBLDialog = true;
        this.ficheBLLoading = true;
        this.fichebl = {};
        const apiUrl = `${url}/custom/aaslink/api/get_fichebl_by_lot.php`;
        try {
          const response = await window.axios.get(apiUrl, {
            params: { lot_id: lotId },
            headers: { 'X-API-KEY': this.dolibarrApiKey },
          });
          if (response.data.status === 'success') {
            this.fichebl = response.data.data;
            this.fichebl.source = source.toUpperCase();
          }
        } catch (e) {
          this.$message.error('Erreur chargement Fiche BL');
        } finally {
          this.ficheBLLoading = false;
        }
      } else if (link.dol_object_type === 'commande') {
        this.commandeDialog = true;
        this.commandeLoading = true;
        this.commande = {};
        const apiUrl = `${url}/custom/aaslink/api/get_commande_by_lot.php`;
        try {
          const response = await window.axios.get(apiUrl, {
            params: { lot_id: lotId },
            headers: { 'X-API-KEY': this.dolibarrApiKey },
          });
          if (response.data.status === 'success') {
            this.commande = response.data.data;
            this.commande.source = source.toUpperCase();
          }
        } catch (e) {
          this.$message.error('Erreur chargement Commande');
        } finally {
          this.commandeLoading = false;
        }
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.tracabilite-descendante {
  padding-bottom: 40px;
}

// ── En-tête ────────────────────────────────────────────────────────
.page-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;

  &__left h2 {
    margin: 0 0 4px;
    font-size: 22px;
    font-weight: 600;
    color: #303133;
  }

  &__subtitle {
    margin: 0;
    color: #909399;
    font-size: 13px;
  }
}

// ── Carte de recherche ─────────────────────────────────────────────
.search-card {
  margin-bottom: 20px;

  .card-header {
    font-weight: 600;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin: 0 0 10px;
}

.search-section {
  margin-bottom: 20px;
}

.search-fields {
  margin-bottom: 16px;
}

.search-form {
  .el-form-item {
    margin-bottom: 10px;
  }
}

.filtres-collapse {
  border: none;
  margin-bottom: 16px;

  ::v-deep .el-collapse-item__header {
    font-size: 13px;
    color: #909399;
  }
}

.search-actions {
  display: flex;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

// ── Chargement ─────────────────────────────────────────────────────
.loading-block {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: #f5f7fa;
  border-radius: 6px;
  color: #606266;
  font-size: 14px;
  margin-bottom: 20px;

  i {
    font-size: 22px;
    color: #409eff;
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}

// ── Résumé résultats ───────────────────────────────────────────────
.results-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  .el-tag {
    font-size: 13px;
  }
}

// ── Accordéon des lots ─────────────────────────────────────────────
.lots-collapse {
  border-radius: 6px;
  overflow: hidden;

  ::v-deep .el-collapse-item__header {
    padding: 0 16px;
    height: auto;
    min-height: 50px;
    background: #fafafa;

    &.is-active {
      background: #ecf5ff;
    }
  }

  ::v-deep .el-collapse-item__content {
    padding: 16px;
  }
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
  padding: 10px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.lot-item {
  margin-bottom: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
}

.lot-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;

  .lot-code {
    font-weight: 700;
    font-size: 14px;
    color: #303133;
    min-width: 130px;
  }

  .lot-date {
    font-size: 13px;
    color: #909399;
  }
}

.lot-body {
  padding: 4px 0;
}

.lot-details {
  margin-bottom: 16px;
}

.lot-chain {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

// ── Documents ──────────────────────────────────────────────────────
.lot-documents {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px 16px;

  .docs-title {
    font-weight: 600;
    font-size: 13px;
    color: #606266;
    margin: 0 0 10px;
  }

  .docs-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .doc-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    color: #606266;
    padding: 6px 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }
  }

  .doc-actions-inline {
    display: flex;
    gap: 5px;
    align-items: center;
  }
}

// ── Drawers ────────────────────────────────────────────────────────
.drawer-content {
  padding: 16px 20px;
  min-height: 200px;
}

.drawer-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  justify-content: space-between;
}

.download-loader {
  margin: 16px 0;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
  text-align: center;
  p {
    margin: 8px 0 0 0;
    font-size: 14px;
    color: #666;
  }
}

.producteur-option {
  display: flex;
  align-items: center;
  line-height: 1.4;
  padding: 2px 0;

  &__name {
    font-weight: 600;
    color: #303133;
  }

  &__group {
    margin-left: auto;
    font-size: 11px;
    color: #c0c4cc;
    font-style: italic;
  }
}

.selected-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #606266;
  margin-top: 4px;
  padding: 5px 10px;
  background: #f0fff4;
  border-left: 3px solid #67c23a;
  border-radius: 0 4px 4px 0;

  strong {
    color: #303133;
  }
}

.dates-helper {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  padding: 6px 10px;
  background: #f0f9ff;
  border-left: 3px solid #409eff;
  border-radius: 0 4px 4px 0;
  width: 100%;

  i {
    color: #409eff;
    flex-shrink: 0;
  }

  strong {
    color: #303133;
  }
}

// ── Carte vide ───────────────────────────────────────────────
.empty-card {
  text-align: center;
  padding: 20px;
}
</style>

